import { zodResolver } from '@hookform/resolvers/zod';
import { FC, Suspense, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import {
	ArrowLeftOutline,
	Button,
	EyeOutline,
	HStack,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	rem,
	Text,
	useToast,
} from '@bonusly/design-system';
import { useCurrentUser } from '@bonusly/who-am-i';

import { useSendInvites } from '../../../../../queries/invites';
import { loadSearchUsers } from '../../../../../queries/users';
import { User } from '../../../../../schemas/users';
import { Bonus } from '../../../../../types/bonus';
import { Reveal } from '../../../../display/reveal';
import InviteDescription from './invite-description';
import { InviteForm, InviteFormData } from './invite-form';
import { InvitePreview } from './invite-preview';
import { createDefaultValues } from './utils';

const createInviteSchema = (t: (key: string) => string) => {
	return z.object({
		subject: z.string().trim().min(1, t('invite_others_modal.validation.subject_required')).max(70, t('invite_others_modal.validation.subject_max_length')),
		title: z.string().trim().min(1, t('invite_others_modal.validation.title_required')).max(40, t('invite_others_modal.validation.title_max_length')),
		body: z.string().trim().min(1, t('invite_others_modal.validation.body_required')).max(400, t('invite_others_modal.validation.body_max_length')),
		inviteList: z
			.array(
				z.object({
					id: z.string(),
					username: z.string(),
					displayName: z.string(),
					email: z.string(),
				})
			)
			.min(1, t('invite_others_modal.validation.invite_list_required')),
	});
};

export interface InviteModalProps {
	isOpen: boolean;
	onClose: () => void;
	bonus: Bonus | undefined;
}

export const InviteModal: FC<InviteModalProps> = ({ isOpen, onClose, bonus }) => {
	const { t } = useTranslation(['recognition']);
	const [isPreviewMode, setIsPreviewMode] = useState(false);
	const toast = useToast();
	const user = useCurrentUser();
	const form = useForm<InviteFormData>({
		defaultValues: createDefaultValues(user?.preferred_first_name || user?.first_name || user?.display_name, bonus, t),
		resolver: zodResolver(createInviteSchema(t)),
	});

	const resetForm = () => {
		form.reset(createDefaultValues(user?.preferred_first_name || user?.first_name || user?.display_name, bonus, t));
	};

	const { mutate: submitInvites, isLoading } = useSendInvites({
		onSuccess: () => {
			toast({ title: t('invite_others_modal.toast.send_success'), status: 'success' });
			resetForm();
			onClose();
		},
		onError: (error: any) => {
			console.error('Error sending invites:', error);
			toast({ title: t('invite_others_modal.toast.send_error'), status: 'error' });
		},
	});

	const handleSubmit = (data: InviteFormData) => {
		if (!bonus?.id) {
			console.error('No bonus ID available');
			return;
		}

		submitInvites({ formData: data, bonusId: bonus.id });
	};

	const handlePreview = () => {
		console.log('Preview clicked:', form.getValues());
		setIsPreviewMode(!isPreviewMode);
	};

	const handleSearch = async (query: string): Promise<User[]> => {
		try {
			const users = await loadSearchUsers({ search: query });
			return users;
		} catch (error) {
			console.error('Error searching users:', error);
			return [];
		}
	};

	if (!isOpen || !bonus) {
		return null;
	}

	const handleClose = () => {
		if (isPreviewMode) {
			setIsPreviewMode(false);
		} else {
			onClose();
		}
	};

	const handleSendNow = async () => {
		const isValid = await form.trigger();
		if (isValid) {
			handleSubmit(form.getValues());
		} else {
			setIsPreviewMode(false);
		}
	};

	return (
		<Modal size={{ base: 'full', md: '2xl' }} autoFocus={false} isOpen={isOpen} onClose={handleClose}>
			<ModalOverlay />
			<ModalContent bgColor={isPreviewMode ? 'bg.subtle' : 'white'} borderRadius="md">
				<ModalHeader p={rem(20)} display="flex" alignItems="center" bgColor="bg.surface" borderTopRadius="md">
					{isPreviewMode ? (
						<>
							<Button onClick={() => setIsPreviewMode(false)} variant="ghost" color="text.soft" p={rem(4)} minW="auto" h="auto" mr={rem(8)}>
								<ArrowLeftOutline size="sm" />
							</Button>
							{t('invite_others_modal.preview_title')}
						</>
					) : (
						t('invite_others_modal.title')
					)}
					<ModalCloseButton top={rem(20)} right={rem(20)} />
				</ModalHeader>

				<ModalBody p={rem(20)} borderRadius="md">
					{!isPreviewMode && <InviteDescription bonus={bonus} />}
					{isPreviewMode ? (
						<Suspense fallback={null}>
							<InvitePreview formData={form.getValues()} />
						</Suspense>
					) : (
						<InviteForm form={form} onSubmit={handleSubmit} onSearchUsers={handleSearch} />
					)}
				</ModalBody>

				<ModalFooter borderTop="1px solid" borderColor="border.default" borderBottomRadius="md" bgColor="bg.surface" pt={4} px={rem(20)}>
					<HStack justify="space-between" width="full">
						<Button colorScheme="secondary" variant="subtle" onClick={onClose} size="md">
							{t('invite_others_modal.cancel')}
						</Button>
						<HStack>
							<Reveal if={!isPreviewMode}>
								<Button color="primary.fg" variant="ghost" onClick={handlePreview}>
									<EyeOutline size="sm" />
									<Text pl={2}>{t('invite_others_modal.preview')}</Text>
								</Button>
							</Reveal>
							<Button isLoading={isLoading} onClick={isPreviewMode ? handleSendNow : () => form.handleSubmit(handleSubmit)()}>
								{t('invite_others_modal.send_now')}
							</Button>
						</HStack>
					</HStack>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};
